<template>
  <!-- <transition name="slide" appear> -->
  <div class="trip-summary-wrapper" ref="root">
    <div class="page" v-if="solution">
      <div class="summary">
        <img :src="solutionImg" :alt="pdfDestinationName" />
        <div class="summary-details">
          <div class="main-details">
            <div>
              <h5 class="summary-hint">Your Trip to</h5>
              <h1 class="summary-main">
                {{ pdfDestinationName }}
              </h1>
              <h3 class="summary-dates">
                {{ solutionDates }}
              </h3>
            </div>
            <div class="filters">
              <div class="travelers-filter">
                <AvatarIcon class="avatar-icon" />
                {{ travelersFilter.value }}
              </div>
              <div class="trip-cost">
                <span>Trip Total </span>
                <span>{{ solutionPrice.labels['USD'] }}<span class="asterix">*</span></span>
              </div>
            </div>
          </div>

          <div class="short-trip-details">
            <div class="flight-details" v-if="airlines">
              <div class="icon-holder">
                <div class="picture-wrapper">
                  <img v-for="img in flightsImg" :src="img" :key="img" alt="flight" />
                </div>
              </div>

              <div class="content flight-content">
                <header class="header">
                  <div class="name">
                    <span v-if="flightDetails.isFlightAvailable.value && isFlightsOn">
                      Fly with <strong>{{ airlines }}</strong></span
                    >
                    <span v-else class="no-flight">No Flights Included</span>
                  </div>
                  <div class="price" v-if="flightDetails.isFlightAvailable.value && isFlightsOn">
                    <div class="price-tag">Full trip</div>
                    <span>{{ flightDetails.price.value }}<span class="asterix">*</span></span>
                  </div>
                </header>
                <div v-if="isFlightsOn">
                  <div class="details-wrapper">
                    <div class="explanation">
                      <FlightDetails
                        v-for="(_, index) in flightDetails.dates.value"
                        :flightDetails="flightDetails.dates.value[index]"
                        direction="Outbound"
                        :hidePrice="true"
                        :key="index"
                      />
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- flight-details over -->
            <div class="flight-details" v-for="stay in allStayActivities" :key="stay.id">
              <div class="icon-holder">
                <div class="picture-wrapper">
                  <img :src="stayImg" :alt="stay.name" />
                </div>
              </div>

              <div class="content">
                <header class="header">
                  <div class="name">
                    <span>{{ isStaysOn ? 'Stay at' : 'Stays' }}</span> <strong>{{ stay.name }}</strong>
                  </div>
                </header>
                <div>
                  <div class="details-wrapper" v-if="isStaysOn">
                    <div class="explanation">
                      <div class="details">
                        {{ getActivityAddress(stay) }}
                      </div>
                      <StarRating
                        class="star-rating"
                        :rating="getActivityRating(stay)"
                        read-only
                        :star-size="12"
                        :increment="0.1"
                      />
                    </div>
                    <div class="price">
                      <div class="price-tag">Avg. Price per Night/</div>
                      <div class="price-tag">all rooms</div>
                      <span>{{ getActivityPrice(stay) }}<span class="asterix">*</span></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- stay-details over -->
            <div class="flight-details" v-if="userSelectedExperincesCaptionDerived">
              <div class="icon-holder">
                <div class="picture-wrapper">
                  <img v-for="img in experincesImg" :key="img" :src="img" alt="eperiences images" />
                </div>
              </div>

              <div class="content">
                <header class="header">
                  <div class="name">Visit <strong v-html="userSelectedExperincesCaptionDerived" /></div>
                </header>
                <div>
                  <div class="details-wrapper">
                    <div class="explanation">
                      <div class="details">Discover the very best of the city curated for your interests.</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- exp details over -->
          </div>
          <div class="price-hint">
            <span class="asterix">*</span>
            Itinerary and pricing is current as of
            <b>{{ currentDate }}</b>
          </div>
        </div>
      </div>
    </div>
    <div class="itinerary-list" v-if="!isLoading">
      <itinerary-list
        :removedItems="removedItems"
        @show-details="openHighlightDetails"
        @remove-item="onRemoveFromTheList"
        @open-category="onOpenCategory"
        @toggle-map-on="onShowMap"
      />
    </div>
  </div>
  <!-- </transition> -->
</template>

<script>
import Vue from 'vue';
import VueCompositionAPI, {computed} from '@vue/composition-api';
import AvatarIcon from '../svg/avatar.vue';
Vue.use(VueCompositionAPI);
import '@mobi-components/amex-bluesky-components/dist/amex-bluesky-components.css';
import {useSummary} from '../summary-page/summary-page-helper';
import {useMainFlight} from '@common/main-flight-card/useMainFlightDetails';
import FlightDetails from '@common/main-flight-card/flight-extended-details.vue';
import StarRating from 'vue-star-rating';

import ItineraryList from './itinerary-list/itinerary-list.vue';
import {lang} from '@mobi-components/global-solution';
import {dropSolution} from '@/store/state/helpers';

export default {
  name: 'AmexSummaryPage',
  components: {
    ItineraryList,
    AvatarIcon,
    StarRating,
    FlightDetails,
  },

  setup(_, {root}) {
    dropSolution();

    const {solution, ...restOfSummary} = useSummary(root, true);

    const flightDetails = computed(() => {
      return useMainFlight();
    });

    const currentDate = new Intl.DateTimeFormat(lang(), {dateStyle: 'long'}).format(new Date());

    return {
      ...restOfSummary,
      flightDetails,
      solution,
      currentDate,
    };
  },
};
</script>

<style scoped lang="scss">
@page :first {
  margin-top: 0;
}
@page {
  margin-top: 50px;
}

.page {
  page-break-after: always;
  height: 100vh;
  overflow: hidden;
}

.summary {
  position: relative;
  box-shadow: inset 0 0 1000px 1000px rgba(0, 0, 0, 0.5);
  height: 100%;
}
.summary > img {
  height: 100vh;
  width: 100vw;
  z-index: 0;
  object-fit: cover;
}

.summary-details {
  position: absolute;
  bottom: 30px;
  // transform: translateY(-50%);
  left: 10vw;
  width: 80vw;
  z-index: 1;
  color: #fff;
  box-shadow: inset 0 0 0 1000px rgba(0, 0, 0, 0.3), 0 0 0 10000px rgba(0, 0, 0, 0.3);
}

.main-details {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
}

.summary-hint {
  font-style: normal;
  font-weight: 400;
  font-size: 17px;
  line-height: 98.1%;
  letter-spacing: -0.005em;
  color: var(--white);
  margin: 0;
}

.summary-main {
  font-style: normal;
  font-weight: 700;
  font-size: 28px;
  line-height: 98.1%;
  letter-spacing: -0.005em;
  color: transparent;
  text-shadow: 0 0 #fff;
  margin: 0;
}

.summary-dates {
  font-style: normal;
  font-weight: 400;
  font-size: 28px;
  line-height: 98.1%;
  letter-spacing: -0.005em;
  color: transparent;
  text-shadow: 0 0 #fff;

  margin: 0;
}

.filters {
  display: flex;
  align-items: center;
}

.avatar-icon {
  color: #a3abb6;
  position: relative;
  top: 2px;
}

.filters > * {
  border-radius: 8px;
  background: #fff;
  padding: 10px;
  margin: 0 5px;
  color: #071d3b;
  font-style: normal;
  font-weight: 400;
  font-size: 11px;
  line-height: 118.14%;
  /* or 13px */

  letter-spacing: -0.005em;
}

.trip-cost {
  color: transparent;
  text-shadow: 0 0 #fff;

  z-index: 1;
  box-shadow: inset 0 0 30px 30px var(--btn-primary-color, hsl(208, 100%, 41%));
}

.short-trip-details {
  background: #ffffff;
  box-shadow: 0px 0px 33px rgba(0, 0, 0, 0.13);
  border-radius: 10px;
  margin-top: 50px;
}

.flight-details {
  display: flex;
  padding: 30px;
}

.flight-details + .flight-details {
  border-top: 1px solid #f1f1f1;
}

.picture-wrapper {
  position: relative;
  width: 75px;
  height: 75px;
  overflow: hidden;
  border-radius: 4px;
  flex-shrink: 0;
  border-radius: 4px;
}

.picture-wrapper > img {
  border-radius: 10px;
  width: 50px;
  height: 50px;
  position: absolute;
  background-color: #fff;
  border: 2px solid #f1f1f1;
}

.picture-wrapper > img:only-child {
  border-radius: 10px;
  width: 75px;
  height: 75px;
  position: absolute;
  background-color: #fff;
  border: 2px solid #f1f1f1;
}

.picture-wrapper img:first-child {
  top: 0;
  left: 0;
}
.picture-wrapper img:last-child {
  bottom: 0;
  right: 0;
}

.details-wrapper {
  flex: 1;
  position: relative;
  margin-left: -10px;
  padding-left: 10px;
  padding-top: 10px;
  display: flex;
}

.explanation {
  flex: 1;
}

.name {
  font-style: normal;
  font-weight: 400;
  font-size: 15px;
  line-height: 118.14%;
  letter-spacing: -0.005em;
  color: #071d3b;
  margin-right: 30px;
}

.details {
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 118.14%;
  letter-spacing: -0.005em;
  color: rgba(7, 29, 59, 0.4);
}

.main-info {
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 118.14%;
  letter-spacing: -0.005em;
  color: #071d3b;
}

.explanation + .price {
  position: relative;
  top: -1em;
}

.price {
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 118.14%;
  text-align: right;
  letter-spacing: -0.005em;
  color: #071d3b;
}

.price-tag {
  font-style: normal;
  font-weight: 400;
  font-size: 10px;
  line-height: 118.14%;
  text-align: right;
  letter-spacing: -0.005em;
  white-space: nowrap;

  color: rgba(7, 29, 59, 0.37);
}

.content {
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-left: 20px;
}
.header {
  display: flex;
  justify-content: space-between;
}

.asterix {
  position: relative;
  top: -2px;
  // left: -2px;
  font-style: normal;
  font-weight: 400;
  font-size: 15px;
  line-height: 118.14%;
  letter-spacing: -0.005em;
}

.price-hint {
  text-align: right;
  margin-top: 15px;
  font-style: normal;
  font-weight: 400;
  font-size: 11px;
  line-height: 118.14%;
  text-align: right;
  letter-spacing: -0.005em;
  text-shadow: 0 0 #fff;
  color: transparent;
}

.price-hint .asterix {
  position: static;
}

.no-flight {
  font-style: normal;
  font-weight: 400;
  font-size: 15px;
  line-height: 118.14%;
  /* identical to box height, or 18px */

  letter-spacing: -0.005em;

  color: rgba(7, 29, 59, 0.6);
}

.flight-content {
  justify-content: center;
}
</style>
